import {
  InterestIncomePreference,
  Investment,
  OpportunityDetails,
  UpdateInvestmentPayload,
} from '../../lib/types'
import { ASSET_CLASSES } from '../../lib/utils/constants'

/**
 * The investment record returned by the API carries server-only metadata keys
 * (e.g. `insufficientBalanceEmailSent`, `carry_rate`) that the PATCH endpoint's
 * validation schema rejects with a 400 if echoed back. Only forward the keys
 * `UpdateInvestmentPayload['metadata']` actually declares.
 */
const UPDATABLE_METADATA_KEYS = [
  'completedSteps',
  'agreement',
  'bankDetailsConfirmed',
  'amountStepCancelled',
  'interestIncomePreference',
  'payFromWallet',
  'walletDebited',
] as const satisfies ReadonlyArray<keyof UpdateInvestmentPayload['metadata']>

export function sanitizeMetadataForUpdate(
  metadata: Investment['metadata'] | undefined,
): UpdateInvestmentPayload['metadata'] {
  const sanitized = {} as UpdateInvestmentPayload['metadata']
  if (!metadata) return sanitized
  for (const key of UPDATABLE_METADATA_KEYS) {
    if (key in metadata) {
      ;(sanitized as Record<string, unknown>)[key] = (metadata as Record<string, unknown>)[key]
    }
  }
  return sanitized
}

export const INVEST_V2_STEPS = [
  { id: 1, label: 'Allocation' },
  { id: 2, label: 'Review' },
  { id: 3, label: 'Fund' },
] as const

export function formatAssetClassLabel(assetClass: string | undefined): string {
  if (!assetClass) return ''
  return assetClass
    .replace(/([A-Z])/g, ' $1')
    .replace(/^ /, '')
    .toUpperCase()
}

export function formatFundingDeadline(closingDate: string | undefined): string {
  if (!closingDate) return '—'
  const d = new Date(closingDate)
  if (Number.isNaN(d.getTime())) return closingDate
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatLockupFromOpportunity(opportunity: OpportunityDetails | undefined): string {
  const days = opportunity?.assetClassData?.lock_in_period_in_days
  if (days === undefined || days === null || days <= 0) return '—'
  const years = Math.round(days / 365)
  if (years >= 1) return `${years} year${years === 1 ? '' : 's'}`
  return `${days} days`
}

export function formatReturnRange(opportunity: OpportunityDetails | undefined): string {
  const min = opportunity?.minimumReturn
  const max = opportunity?.maximumReturn
  if (min && max) return `${min}% - ${max}%`
  if (opportunity?.irr) return opportunity.irr
  return '—'
}

/** Format opportunity IRR for display (e.g. Deal Recap Interest). */
export function formatInterestPercent(value?: string | null): string {
  if (!value) return '—'
  const cleaned = String(value).replace(/[^0-9.-]/g, '')
  if (!cleaned) return value.includes('%') ? value : `${value}%`
  const numeric = Number(cleaned)
  if (Number.isNaN(numeric)) return value.includes('%') ? value : `${value}%`
  return `${numeric.toFixed(2)}%`
}

export function formatManagementFee(opportunity: OpportunityDetails | undefined): string {
  const p = opportunity?.assetClassData?.management_fee_percent
  if (p === undefined || p === null) return '—'
  return `${p}% p.a.`
}

export function formatPerformanceFee(opportunity: OpportunityDetails | undefined): string {
  const p = opportunity?.assetClassData?.profit_sharing_to_credibila_percent
  if (p === undefined || p === null) return '—'
  return `${p}%`
}

/** Carried interest / profit share — same source as performance fee (API). */
export function formatCarriedInterest(opportunity: OpportunityDetails | undefined): string {
  return formatPerformanceFee(opportunity)
}

/** Distribution frequency from `coupon_payment_interval_in_months` when present. */
export function formatDistributionFromOpportunity(
  opportunity: OpportunityDetails | undefined,
): string {
  const m = opportunity?.couponPaymentIntervalInMonths
  if (m === undefined || m === null) return '—'
  if (m === 1) return 'Monthly'
  if (m === 3) return 'Quarterly'
  if (m === 6) return 'Semi-annual'
  if (m === 12) return 'Annual'
  return `Every ${m} months`
}

export function formatFeesSummaryLine(
  investmentAmount: number,
  totalFees: number | undefined,
): string {
  if (
    totalFees === undefined ||
    totalFees === null ||
    investmentAmount <= 0 ||
    !Number.isFinite(totalFees) ||
    !Number.isFinite(investmentAmount)
  ) {
    return '—'
  }
  const pct = (totalFees / investmentAmount) * 100
  if (!Number.isFinite(pct) || pct <= 0) return '—'
  return `${pct.toFixed(1)}%`
}

/** Normalize API / stored metadata into a known preference (default: payout). */
export function parseInterestIncomePreference(metadata: unknown): InterestIncomePreference {
  if (!metadata || typeof metadata !== 'object') return 'payout'
  const m = metadata as Record<string, unknown>
  const raw = m.interestIncomePreference ?? m.interest_income_preference
  if (raw === 'reinvestment' || raw === 'payout') return raw
  if (typeof raw === 'string') {
    const lower = raw.toLowerCase()
    if (lower === 'reinvestment' || lower === 'reinvest') return 'reinvestment'
    if (lower === 'payout') return 'payout'
  }
  return 'payout'
}

export function labelInterestIncomePreference(pref: InterestIncomePreference): string {
  return pref === 'reinvestment' ? 'Reinvestment' : 'Payout to bank'
}

/** Fixed income–style opportunities where interest can be paid out or reinvested (e.g. Fixed Income, Trade Finance). */
export function opportunitySupportsInterestIncomePreference(
  assetClass: string | undefined,
): boolean {
  if (!assetClass) return false
  return assetClass === ASSET_CLASSES.FIXED_INCOME || assetClass === ASSET_CLASSES.TRADE_FINANCE
}

export function isTransferPageEnabled(
  opportunity?: Pick<OpportunityDetails, 'transferPageEnabled'>,
): boolean {
  return opportunity?.transferPageEnabled !== false
}

export function showInterestIncomePreferenceOption(
  opportunity: Pick<OpportunityDetails, 'assetClass'>,
): boolean {
  return opportunitySupportsInterestIncomePreference(opportunity.assetClass)
}

/**
 * Remaining investable capacity in dollars from API `canInvest` (cents),
 * plus the current draft investment amount when editing an in-progress investment.
 */
export function getRemainingCanInvestAmountUsd(
  canInvestCents: string | number | null | undefined,
  existingInvestmentAmountCents?: string | number | null,
): number {
  const remaining = Number(canInvestCents) / 100
  const existing = Number(existingInvestmentAmountCents) / 100
  const total =
    Number.isFinite(existing) && existing > 0
      ? (Number.isFinite(remaining) ? remaining : 0) + existing
      : Number.isFinite(remaining)
      ? remaining
      : 0
  return Number.isFinite(total) ? total : 0
}

export function exceedsRemainingInvestmentLimit(
  amountUsd: number,
  remainingCanInvestUsd: number,
): boolean {
  if (!Number.isFinite(amountUsd) || !Number.isFinite(remainingCanInvestUsd)) return false
  return amountUsd > remainingCanInvestUsd
}

/**
 * Format a money amount while typing: digits, optional `.`, up to 2 decimal places,
 * thousand separators on the integer part. Preserves a trailing `.` so decimals can be entered.
 */
export function formatUsdAmountTyping(input: string): string {
  const cleaned = input.replace(/[^\d.]/g, '')
  const firstDot = cleaned.indexOf('.')
  let intPart = firstDot === -1 ? cleaned : cleaned.slice(0, firstDot)
  const decRaw = firstDot === -1 ? null : cleaned.slice(firstDot + 1).replace(/\./g, '')
  const decPart = decRaw === null ? null : decRaw.slice(0, 2)
  intPart = intPart.replace(/^0+(?=\d)/, '')
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  if (firstDot === -1) return withCommas
  return `${withCommas}.${decPart ?? ''}`
}

/** Display a numeric USD amount with commas; keep up to 2 decimal places when needed. */
export function formatUsdAmountFromNumber(value: number): string {
  if (!Number.isFinite(value)) return '0'
  const cents = Math.round(value * 100)
  const negative = cents < 0
  const abs = Math.abs(cents)
  const intPart = String(Math.floor(abs / 100))
  const decPart = String(abs % 100).padStart(2, '0')
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const signed = negative ? `-${withCommas}` : withCommas
  return decPart === '00' ? signed : `${signed}.${decPart}`
}

/** True when the display string is mid-decimal entry for the same numeric value. */
export function isUsdAmountTypingInProgress(display: string, value: number): boolean {
  const raw = display.replace(/,/g, '')
  if (!raw.includes('.')) return false
  const parsed = Number(raw.endsWith('.') ? raw.slice(0, -1) : raw)
  if (!Number.isFinite(parsed) || !Number.isFinite(value)) return false
  if (Math.round(parsed * 100) !== Math.round(value * 100)) return false
  return raw.endsWith('.') || /^\d*\.\d{0,2}$/.test(raw)
}
